/**
 * HTML-in-text rendering shared by the Signals description and the Actions-rail
 * payload (`FactModule`): SFDC descriptions and generated payload fields arrive
 * as either plain text or an HTML fragment.
 *
 * `looksLikeHtml` is deliberately a TAG heuristic, not an entity one: a stray
 * `<` in prose ("spend < 30 min") does not match, `<b>`, `<a href…>`, `<br/>` do.
 * Detection is HEURISTIC — sanitization, not detection, is the security
 * boundary, so a false positive only costs plain text a DOMPurify pass, never an
 * injection. There is no explicit "this field is HTML" signal in the payload
 * schema today; if one is added, prefer it over this sniff (do not hardcode a
 * key list here).
 */
import DOMPurify, { type Config } from 'dompurify';

/** An opening/closing/self-closing tag with a real element name. */
const HTML_TAG_RE = /<\/?[a-z][a-z0-9-]*(\s[^<>]*)?\/?>/i;

export function looksLikeHtml(text: string): boolean {
  return HTML_TAG_RE.test(text);
}

/**
 * Sanitize HTML (the security boundary for `dangerouslySetInnerHTML`) and force
 * every anchor to `target="_blank" rel="noopener noreferrer"` so a link can
 * never navigate the app shell or reach back via window.opener. The
 * template-element pass runs AFTER DOMPurify so the attributes we add can't be
 * stripped, and we never install a global DOMPurify hook (RichHtmlField shares
 * the module). `config` defaults to DOMPurify's html profile.
 */
export function sanitizeHtml(html: string, config: Config = { USE_PROFILES: { html: true } }): string {
  const clean = DOMPurify.sanitize(html, config);
  const tpl = document.createElement('template');
  tpl.innerHTML = clean;
  for (const anchor of Array.from(tpl.content.querySelectorAll('a'))) {
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
  }
  return tpl.innerHTML;
}
